import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface PayrollClient {
  payrollClientId: string;
  ceanr: number;
  name: string;
}

export interface Institution {
  institutionId: string;
  payrollClientId: string;
  name: string;
  code: number;
  changeUser: string;
  changeDateTime: string;
}

export interface ImportRun {
  importRunId: string;
  institutionId: string;
  institutionName: string;
  institutionCode: number;
  ceanr: number;
  year: number;
  period: number;
  payrollRunNumber: number;
  payrollSequence: number;
  importStatus: string;
  importDatetime: string;
  deleteDate: string;
  toBeDeleted: boolean;
  changeUser: string;
  changeDateTime: string;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollRunService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/Processing';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the payroll clients (opdrachtgevers) available to the current user.
   */
  getPayrollClients(): Observable<PayrollClient[]> {
    return this.http
      .get<PayrollClient[] | null>(this.apiUrl + '/PayrollClients', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the institutions (instellingen) for a payroll client, identified by its ceanr.
   */
  getInstitutions(ceaNr: number): Observable<Institution[]> {
    return this.http
      .get<Institution[] | null>(
        this.apiUrl + '/PayrollClient/' + ceaNr + '/Institutions',
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the import runs for an institution, identified by its code.
   */
  getImportRuns(code: number): Observable<ImportRun[]> {
    return this.http
      .get<ImportRun[] | null>(
        this.apiUrl + '/PayrollClient/Institution/' + code + '/ImportRuns',
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
